import axios from 'axios';
import { readEnv } from '../../config/env';
import { unifiedWebSocketService } from '../unifiedWebSocket';
import { useOptimizationStore } from '../../store/optimizationStore';
import { API_BASE_URL } from '../config';

export function createApiClient(baseURL: string) {
  const client = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true, // Поддержка куков
  });

  // Глобальные перехватчики ответов API для авто-очистки при выходе
  client.interceptors.response.use(
    (response) => {
      try {
        const data = response?.data as any;
        if (
          data &&
          data.success === true &&
          data.data &&
          typeof data.data.message === 'string' &&
          data.data.message.toLowerCase() === 'signed out'
        ) {
          performGlobalSignoutCleanup();
        }
      } catch {}
      return response;
    },
    (error) => {
      // Любые 401 считаем потерей сессии — делаем безопасную очистку.
      // Кроме внутреннего контура коннектора: он авторизуется сервисным ключом, а не
      // кукой пользователя, поэтому его 401 говорит про ключ и ничего про сессию.
      // Без этой оговорки отклонённый ключ выкидывал бы пользователя из Тюнера.
      const url = String(error?.config?.url || '');
      const serviceAuthenticated = url.includes('/connector/internal/');
      if (error?.response?.status === 401 && !serviceAuthenticated) {
        performGlobalSignoutCleanup();
        // Синхронизируем React AuthContext (interceptors не знают о хуках)
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('infiano:session-expired'));
        }
      }
      return Promise.reject(error);
    }
  );

  return client;
}

export const api = createApiClient(API_BASE_URL);

/** Dev-only workflow test chat. Absent flag = the feature does not exist in that build. */
export function workflowTestChatEnabled(): boolean {
  return readEnv('REACT_APP_WORKFLOW_TEST_CHAT') === '1';
}

/**
 * Глобальная очистка состояния приложения при выходе пользователя.
 * Используется как при явном logout, так и при ответах API вида
 * { success: true, data: { message: "signed out" } } или статусе 401.
 */
export const performGlobalSignoutCleanup = () => {
  try {
    // Отключаем все активные сессии и чистим сохранённые run_id
    unifiedWebSocketService.disconnect(true);
  } catch (e) {
    console.warn('WS cleanup on signout failed:', e);
  }

  try {
    // Полная очистка zustand-хранилища от временных данных страниц
    const store = useOptimizationStore.getState();

    // Чаты
    store.clearSingleAgentChat();
    store.clearMultiAgentChat();

    // Single-agent оптимизация
    store.resetOptimization();
    store.setIsOptimized(false);
    store.setOptimizationProgress(0);

    // Графики/данные
    store.clearGraphData();

    // Multi-agent оптимизация
    store.setMultiAgentOptimizedPrompts([]);
    store.setMultiAgentIterationResults([]);
    store.setMultiAgentIsOptimized(false);
    store.setMultiAgentSelectedIterationIndex(0);
    store.setMultiAgentIsOptimizing(false);
    store.setMultiAgentOptimizationProgress(0);

    // Общие поля/вводы
    store.setInitialPrompt('');
    store.setBotPrompt('');
    store.setSelectedPrompt('');
    store.setSelectedPromptIndex('');

    // Сбрасываем выбор датасета, чтобы исключить зависшие состояния страниц
    store.setSelectedDataset('');

    // На уровне persist очищаем сохранённую копию
    // (на следующем монтировании дефолты подтянутся заново)
    // В некоторых окружениях persist может быть недоступен - проверяем опционально
    // @ts-ignore
    useOptimizationStore.persist?.clearStorage?.();
  } catch (e) {
    console.warn('Store cleanup on signout failed:', e);
  }
};

/**
 * Body of an API answer whether it comes in the `{ success, data }` envelope or bare —
 * one place for the `res.data?.data ?? res.data` guessing callers used to do (AIT-425).
 */
export function unwrapApiData<T>(body: unknown): T | null {
  if (!body || typeof body !== 'object') return null;
  const envelope = body as { success?: unknown; data?: unknown };
  if ('success' in envelope && 'data' in envelope) return (envelope.data ?? null) as T | null;
  return body as T;
}
